import { useQueryClient } from '@tanstack/react-query';
import {
  failureFromResponse,
  type ChangeExpectByRequest,
  type CloseCommitmentRequest,
  type ClosedCommitmentDto,
  type CommitmentAnnotationDto,
  type CommitmentCheckInRequest,
  type OpenCommitmentDto,
} from '@signalvote/shared';
import { apiJson, type ApiErrorBody } from './apiFetch';
import { invalidateAroundBooks, markBooksStale, patchBookRows } from './prospectBookCache';
import { campaignHeaders, defineQuery, defineWrite, edit } from '../../lib/data';

const COMMITMENTS_PATH = '/api/fundraising/commitments';

/**
 * The Fundraiser's chase list (Fundraising: Manage): Open Commitments, soonest expect-by first. A write
 * naming a commitment, its check-ins or its person refreshes it.
 */
export const useOpenCommitments = defineQuery({
  key: () => ['fundraising', 'open-commitments'],
  reads: 'GET /api/fundraising/commitments',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<OpenCommitmentDto[]>(COMMITMENTS_PATH, { signal, headers: campaignHeaders(campaignId), error: 'Failed to fetch the open commitments' }),
});

const send = <T>(path: string, method: 'POST' | 'PUT', json: unknown, fallback: string) =>
  apiJson<T>(`${COMMITMENTS_PATH}/${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    json,
    // A JSON `null` body reaches here as null, so keep the optional chain.
    error: (body: ApiErrorBody | null, response) =>
      failureFromResponse(response, body, body?.error || body?.message ? undefined : fallback),
  });

/*
 * Commitment changes are writes to one versioned row: each runs behind the last change to the same
 * commitment and sends the newest token this client holds for it, so a quick second change never
 * false-conflicts with the first. A refusal rejects with the server's message.
 */

const closeCommitment = defineWrite({
  row: 'Commitment',
  send: (id, concurrencyVersion: number, request: Omit<CloseCommitmentRequest, 'concurrencyVersion'>) =>
    send<ClosedCommitmentDto>(`${id}/close`, 'POST', { ...request, concurrencyVersion }, "Couldn't close the commitment. Try again."),
});

/**
 * Close one for good with a Fundraiser's reason. The chase list refreshes from the write set. The
 * Prospects book and the reads around it are not defined reads yet, so the close still patches the
 * person's row into every cached book and re-reads a person's page and the tab counts by hand.
 */
export function useCloseCommitment() {
  const queryClient = useQueryClient();
  return closeCommitment({
    onSuccess: (closed) => {
      if (closed.row) patchBookRows(queryClient, [closed.row]);
    },
    onSettled: () => {
      void invalidateAroundBooks(queryClient);
      void markBooksStale(queryClient);
    },
  });
}

const landAnnotation = (saved: CommitmentAnnotationDto) => [
  edit(useOpenCommitments, undefined, (rows) =>
    rows.map((c) =>
      c.id === saved.id
        ? { ...c, expectBy: saved.expectBy, latestCheckIn: saved.latestCheckIn, concurrencyVersion: saved.concurrencyVersion }
        : c,
    ),
  ),
];

/** Log a check-in (a short note, who and when) on an Open Commitment. */
export const useCheckInCommitment = defineWrite({
  row: 'Commitment',
  send: (id, concurrencyVersion: number, { note }: { note: string }) =>
    send<CommitmentAnnotationDto>(
      `${id}/check-ins`,
      'POST',
      { note, concurrencyVersion } satisfies CommitmentCheckInRequest,
      "Couldn't save the check-in. Try again.",
    ),
  land: landAnnotation,
});

/** Set or clear when to expect an Open Commitment; the list regroups on the new date. */
export const useChangeCommitmentExpectBy = defineWrite({
  row: 'Commitment',
  send: (id, concurrencyVersion: number, { expectBy }: { expectBy: string | null }) =>
    send<CommitmentAnnotationDto>(
      `${id}/expect-by`,
      'PUT',
      { expectBy, concurrencyVersion } satisfies ChangeExpectByRequest,
      "Couldn't change the expected date. Try again.",
    ),
  land: landAnnotation,
});
